// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.6.1
//   protoc               unknown
// source: booking-server.proto

/** API v3 public interface declaration */

/**
 * Status of a booking.
 *
 * Updating booking status does not change the status of the associated payment.
 * Prepayment status updates should be done using the PrepaymentStatus enum.
 */
export enum BookingStatus {
	/** BOOKING_STATUS_UNSPECIFIED - Not specified. */
	BOOKING_STATUS_UNSPECIFIED = 'BOOKING_STATUS_UNSPECIFIED',
	/** CONFIRMED - Booking has been confirmed */
	CONFIRMED = 'CONFIRMED',
	/**
	 * PENDING_MERCHANT_CONFIRMATION - Booking is awaiting confirmation by the merchant before it can transition
	 * into CONFIRMED status. Only applicable to non-payments Dining or
	 * Beauty verticals.
	 */
	PENDING_MERCHANT_CONFIRMATION = 'PENDING_MERCHANT_CONFIRMATION',
	/**
	 * CANCELED - Booking has been canceled on behalf of the user.
	 * The merchant can still trigger a manual refund.
	 */
	CANCELED = 'CANCELED',
	/** NO_SHOW - User did not show for the appointment */
	NO_SHOW = 'NO_SHOW',
	/**
	 * NO_SHOW_PENALIZED - User did not show for the appointment in violation of the cancellation
	 * policy.
	 */
	NO_SHOW_PENALIZED = 'NO_SHOW_PENALIZED',
	/** FAILED - Booking could not be completed by the async backend due to a failure. */
	FAILED = 'FAILED',
	/**
	 * DECLINED_BY_MERCHANT - Booking was asynchronously declined by the merchant. Only applicable to
	 * non-payments Dining or Beauty verticals.
	 */
	DECLINED_BY_MERCHANT = 'DECLINED_BY_MERCHANT',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/**
 * Used when booking/order failure cause is PAYMENT_ERROR_CARD_TYPE_REJECTED to
 * indicate the type of credit card that was rejected.
 */
export enum CreditCardType {
	/** CREDIT_CARD_TYPE_UNSPECIFIED - Default value. Used if credit card type does not match to one below. */
	CREDIT_CARD_TYPE_UNSPECIFIED = 'CREDIT_CARD_TYPE_UNSPECIFIED',
	VISA = 'VISA',
	MASTERCARD = 'MASTERCARD',
	AMERICAN_EXPRESS = 'AMERICAN_EXPRESS',
	DISCOVER = 'DISCOVER',
	JCB = 'JCB',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

export enum PaymentOptionType {
	PAYMENT_OPTION_TYPE_UNSPECIFIED = 'PAYMENT_OPTION_TYPE_UNSPECIFIED',
	PAYMENT_OPTION_SINGLE_USE = 'PAYMENT_OPTION_SINGLE_USE',
	PAYMENT_OPTION_MULTI_USE = 'PAYMENT_OPTION_MULTI_USE',
	PAYMENT_OPTION_UNLIMITED_USE = 'PAYMENT_OPTION_UNLIMITED_USE',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/**
 * Prepayment status of a booking.
 * Updating payment status will trigger an update on the payment status of the
 * associated booking (if applicable).
 * Currently, the only supported transition is from PREPAYMENT_PROVIDED to
 * PREPAYMENT_REFUNDED, which will initiate a non-reversible refund on the
 * associated payment transaction.
 */
export enum PrepaymentStatus {
	/** PREPAYMENT_STATUS_UNSPECIFIED - Not specified, defaults to PREPAYMENT_NOT_PROVIDED. */
	PREPAYMENT_STATUS_UNSPECIFIED = 'PREPAYMENT_STATUS_UNSPECIFIED',
	/** PREPAYMENT_PROVIDED - The fee for the booking has been paid in advance. */
	PREPAYMENT_PROVIDED = 'PREPAYMENT_PROVIDED',
	/** PREPAYMENT_NOT_PROVIDED - The fee for the booking has not been paid in advance. */
	PREPAYMENT_NOT_PROVIDED = 'PREPAYMENT_NOT_PROVIDED',
	/** PREPAYMENT_REFUNDED - The fee was previously PREPAYMENT_PROVIDED but has now been refunded. */
	PREPAYMENT_REFUNDED = 'PREPAYMENT_REFUNDED',
	/**
	 * PREPAYMENT_CREDITED - The fee was previously PREPAYMENT_PROVIDED but now has been credited
	 * (user given a UserPaymentOption as a voucher for the booking).
	 * If this is set, the response should also include the updated
	 * UserPaymentOption.
	 */
	PREPAYMENT_CREDITED = 'PREPAYMENT_CREDITED',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/** Defines how a total price is determined from an availability. */
export enum PriceType {
	/**
	 * FIXED_RATE_DEFAULT - The price is for a fixed amount. This is the default value if the field is
	 * not set.
	 *
	 * Examples:
	 *   $50 deposit to reserve a table; $20 no show fee for a yoga class
	 */
	FIXED_RATE_DEFAULT = 'FIXED_RATE_DEFAULT',
	/**
	 * PER_PERSON - The price specified is per person, and the total price is calculated
	 * according to the party size specified in Resources as price_micros *
	 * party_size. A PER_PERSON price must be accompanied by a party size in the
	 * availability resources. If it is not, a party size of one is used.
	 *
	 * Examples:
	 *   $10 each for tickets to a museum
	 */
	PER_PERSON = 'PER_PERSON',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/** Mode by which bookings for an availability slot are confirmed. */
export enum ConfirmationMode {
	/**
	 * CONFIRMATION_MODE_UNSPECIFIED - The confirmation mode was not specified.
	 * Synchronous confirmation will be assumed.
	 */
	CONFIRMATION_MODE_UNSPECIFIED = 'CONFIRMATION_MODE_UNSPECIFIED',
	/** CONFIRMATION_MODE_SYNCHRONOUS - Bookings for this availability will be confirmed synchronously. */
	CONFIRMATION_MODE_SYNCHRONOUS = 'CONFIRMATION_MODE_SYNCHRONOUS',
	/** CONFIRMATION_MODE_ASYNCHRONOUS - Bookings for this availability will be confirmed asynchronously. */
	CONFIRMATION_MODE_ASYNCHRONOUS = 'CONFIRMATION_MODE_ASYNCHRONOUS',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

export interface BatchAvailabilityLookupRequest {
	/** ID of the merchant. */
	merchant_id?: string | undefined
	/**
	 * Multiple slot times to be checked for availability. All queried times apply
	 * to the same merchant_id and service_id.
	 */
	slot_time?: SlotTime[] | undefined
}

/**
 * Response for the [ext.maps.booking.partner.v3.BatchAvailabilityLookupRequest]
 * RPC with the availabilities of the appointment slots.
 */
export interface BatchAvailabilityLookupResponse {
	/**
	 * The availabilities for the requested SlotTime entries. There must be
	 * exactly one slot_time_availability for each SlotTime entry in the
	 * [ext.maps.booking.partner.v3.BatchAvailabilityLookupRequest].
	 */
	slot_time_availability?: SlotTimeAvailability[] | undefined
}

/**
 * Identifies a Slot service_id and start time and optionally, the Slot duration
 * and resources, for a specific merchant. Note that this differs from the
 * definition of Slot, as it does not include merchant_id identifier.
 */
export interface SlotTime {
	/** ID of the service. (required) */
	service_id?: string | undefined
	/**
	 * Start time of the appointment slot in seconds of UTC time since Unix epoch
	 * (required)
	 */
	start_sec?: string | undefined
	/** Duration of the appointment slot in seconds (optional) */
	duration_sec?: string | undefined
	/**
	 * Opaque tag that identifies the availability slot and matches the value
	 * provided in the availability feed (optional)
	 */
	availability_tag?: string | undefined
	/**
	 * The set of resources that specifies the appointment slot, e.g. by
	 * indicating the staff member and room selected by the user, or party size
	 * for dining slots (optional)
	 */
	resource_ids?: ResourceIds | undefined
	/**
	 * Indicates whether bookings of this slot will be confirmed
	 * synchronously or asynchronously. (optional)
	 * An UNSPECIFIED value will be interpreted as synchronous.
	 */
	confirmation_mode?: ConfirmationMode | undefined
}

export interface SlotTimeAvailability {
	/** The SlotTime for which availability was checked. */
	slot_time?: SlotTime | undefined
	/** Whether the requested SlotTime is available */
	available?: boolean | undefined
}

/** Request to check availability for a Slot. */
export interface CheckAvailabilityRequest {
	/** The appointment slot that is being checked (required) */
	slot?: Slot | undefined
}

/**
 * Response for the CheckAvailability RPC with the availability of the
 * appointment slot.
 */
export interface CheckAvailabilityResponse {
	/** The requested slot. (required) */
	slot?: Slot | undefined
	/**
	 * Number of available spots.
	 * 0 indicates that the appointment slot is not available. (required)
	 */
	count_available?: number | undefined
	/**
	 * If set, the last time (in seconds since the Unix epoch) that this specific
	 * appointment slot can be cancelled through Reserve with Google. This field
	 * will override any service-level cancellation rules. (optional)
	 *
	 * Deprecated: Use scheduling_rule_overrides.last_online_cancellable_sec
	 * in the Availability feed instead.
	 *
	 * @deprecated
	 */
	last_online_cancellable_sec?: string | undefined
	/**
	 * The requirement to show the slots duration and/or endtime. This field will
	 * be ignored if the slot is unavailable. (optional)
	 *
	 * Deprecated: Use duration_requirement in the Availability feed instead.
	 *
	 * @deprecated
	 */
	duration_requirement?: CheckAvailabilityResponse_DurationRequirement | undefined
	/**
	 * Optionally, the partner can return additional updated information about the
	 * availability for this merchant if this information is present when
	 * responding to the CheckAvailabilityRequest and if there is no negative
	 * impact on the CheckAvailability request latency.
	 * For instance an entire day of merchant availability for a superset of
	 * resources can be returned here.
	 */
	availability_update?: AvailabilityUpdate | undefined
}

/**
 * This enum indicates what requirements exist for the user to acknowledge or
 * view the requested slots duration/end time.
 */
export enum CheckAvailabilityResponse_DurationRequirement {
	/** DURATION_REQUIREMENT_UNSPECIFIED - The handling of the end time is not specified. This is the default. */
	DURATION_REQUIREMENT_UNSPECIFIED = 'DURATION_REQUIREMENT_UNSPECIFIED',
	/** DO_NOT_SHOW_DURATION - The end time is not shown to the user. */
	DO_NOT_SHOW_DURATION = 'DO_NOT_SHOW_DURATION',
	/**
	 * MUST_SHOW_DURATION - The end time has to be shown to the user before an appointment can be
	 * made.
	 */
	MUST_SHOW_DURATION = 'MUST_SHOW_DURATION',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/**
 * An update to one ore more slots indicating that the availability for the
 * associated time has potentially changed.
 */
export interface AvailabilityUpdate {
	slot_availability?: SlotAvailability[] | undefined
}

/** An inventory slot and associated count of open spots. */
export interface SlotAvailability {
	slot?: Slot | undefined
	/**
	 * Number of available spots.
	 * 0 indicates that the appointment slot is not available. (required)
	 */
	count_available?: number | undefined
}

/** Request to get booking status and prepayment status for a Booking. */
export interface GetBookingStatusRequest {
	/** ID of the existing booking (required) */
	booking_id?: string | undefined
}

/**
 * Response for the GetBookingStatus RPC with booking status and prepayment
 * status.
 */
export interface GetBookingStatusResponse {
	/** ID of the booking (required) */
	booking_id?: string | undefined
	/** Status of the booking (required) */
	booking_status?: BookingStatus | undefined
	/** Prepayment status of the booking (required) */
	prepayment_status?: PrepaymentStatus | undefined
}

/**
 * Request to create a Booking for an inventory slot. Consumes the lease if
 * provided.
 */
export interface CreateBookingRequest {
	/**
	 * The inventory slot that is being requested to make this booking.
	 * If lease_ref is provided, slot must match the lease; slot is provided for
	 * the partner to verify the lease information.
	 * If lease_ref is absent, then create the booking for the slot. (required)
	 */
	slot?: Slot | undefined
	/** Personal information of the user making the appointment (required) */
	user_information?: UserInformation | undefined
	/**
	 * Information about payments. When payment authorizations are handled by
	 * Google, if the booking request does not succeed, payment authorizations are
	 * automatically canceled. (optional)
	 */
	payment_information?: PaymentInformation | undefined
	/**
	 * The parameters to be used if the payment is processed by the partner
	 * (i.e. payment_information.payment_processed_by is equal to
	 * PROCESSED_BY_PARTNER). (optional)
	 */
	payment_processing_parameters?: PaymentProcessingParameters | undefined
	/**
	 * Idempotency token for CreateBooking requests. (required)
	 *
	 * This uniquely identifies a booking request. Specifically:
	 * - If a booking was already created for a CreateBooking request with this
	 * idempotency token, that booking should be returned.
	 * - If no booking has been created for a CreateBooking request with this
	 * idempotency token, this should be considered to be a request for a new
	 * booking, and **no previously-created booking may be returned**.
	 *
	 * If a partner considers the requested booking to be a duplicate of a
	 * previously-created booking, this request should fail with reason
	 * BookingFailure.OVERLAPPING_RESERVATION. The partner is responsible for
	 * determining whether this duplicates a previous booking, but for example the
	 * partner may consider a booking request to be a duplicate if an existing
	 * booking has the same party size, time, and email address.
	 */
	idempotency_token?: string | undefined
	/**
	 * A string from the user which contains any special requests or additional
	 * information that they would like to notify the merchant about. (optional)
	 */
	additional_request?: string | undefined
	/**
	 * Partner provided offer id associated with this slot. (optional)
	 *
	 * If the offer is no longer available, the booking should not be made, and in
	 * response.booking_failure should be set to OFFER_UNAVAILABLE.
	 */
	offer_id?: string | undefined
}

/** Response with the created Booking for an inventory slot. */
export interface CreateBookingResponse {
	/** The created booking (required) */
	booking?: Booking | undefined
	/**
	 * The updated user payment option used in this booking.
	 * If a new payment option was purchased to pay for the booking, this should
	 * be a newly created user payment option.
	 * If an already purchased user payment option was used for this booking,
	 * this should reflect an updated version of that user payment option.
	 * (optional)
	 */
	user_payment_option?: UserPaymentOption | undefined
	/**
	 * If creating a booking fails, this field should reflect the business logic
	 * error (e.g., slot has become unavailable) and all other fields in the
	 * CreateBookingResponse message are expected to be unset. (required if
	 * failure occurs)
	 */
	booking_failure?: BookingFailure | undefined
}

/**
 * Request to create a Lease for a slot in the inventory.  The expiration time
 * in the returned Lease may be modified by the backend, e.g. if the requested
 * lease period is too long.
 */
export interface CreateLeaseRequest {
	/**
	 * The lease to be created with information about the appointment slot
	 * (required)
	 */
	lease?: Lease | undefined
}

/** Response for the CreateLease RPC with the created Lease. */
export interface CreateLeaseResponse {
	/** The created Lease (required) */
	lease?: Lease | undefined
	/**
	 * If creating a lease fails, this field should reflect the business logic
	 * error (e.g., slot has become unavailable) and lease field is expected to be
	 * unset. (required if failure occurs)
	 */
	booking_failure?: BookingFailure | undefined
}

/** Temporary lease for an inventory slot */
export interface Lease {
	/**
	 * ID of the lease.
	 * Not populated in CreateLeaseRequest. The value is chosen by the partner and
	 * has to be returned in the response of CreateLease. (required)
	 */
	lease_id?: string | undefined
	/** The appointment slot that the lease is created for. (required) */
	slot?: Slot | undefined
	/**
	 * Unique identifier for this lease, chosen by Reserve with Google. Serves as
	 * an idempotency token for CreateLease requests. (required)
	 */
	user_reference?: string | undefined
	/** Expiration time of the lease in UTC Timestamp (required) */
	lease_expiration_time_sec?: string | undefined
}

/** Reference to a Lease that has been created via CreateLease. */
export interface LeaseReference {
	/** Lease ID (required) */
	lease_id?: string | undefined
}

/** Request to list all bookings for a user */
export interface ListBookingsRequest {
	/** ID of the user (required) */
	user_id?: string | undefined
}

/** Response for the ListBookings RPC with all bookings for the requested user. */
export interface ListBookingsResponse {
	/** All bookings of the user (required) */
	bookings?: Booking[] | undefined
}

/** Request to update a Booking. */
export interface UpdateBookingRequest {
	/**
	 * The booking to be updated
	 * The following fields can be set in a booking:
	 * - status, to cancel a booking.
	 * - one of the following is required:
	 *   - start_time AND duration in the slot, to reschedule a booking.
	 *   - party_size (for dining reservations).
	 */
	booking?: Booking | undefined
}

/** Response with the updated Booking. */
export interface UpdateBookingResponse {
	/** The updated booking (required) */
	booking?: Booking | undefined
	/**
	 * The updated user payment option originally used to pay for this booking.
	 * This should be set if the UpdateBookingRequest results in a change to
	 * the UserPaymentOption.
	 * For instance, if the booking is canceled, the UserPaymentOption should
	 * reflect an additional credit to the user. In the case of a multi-use
	 * payment option, the current_count should be increased by one to
	 * allow the user to create another booking with this payment option. In the
	 * case of a single-use payment option, a new single-use user payment option
	 * should be returned. (required if altered in update)
	 */
	user_payment_option?: UserPaymentOption | undefined
	/**
	 * If updating a booking fails, this field should reflect the business logic
	 * error (e.g., booking is not cancellable) (required if failure occurs)
	 */
	booking_failure?: BookingFailure | undefined
}

/** Request to set the partner marketing preference for a user. */
export interface SetMarketingPreferenceRequest {
	/** Personal information of the user setting a marketing preference (required) */
	user_information?: UserInformation | undefined
	/** Whether the specified user has consented to receive marketing (required) */
	user_to_receive_marketing?: boolean | undefined
}

/** Response with the user's set marketing preference */
export interface SetMarketingPreferenceResponse {
	/** Personal information of the user setting a marketing preference (required) */
	user_information?: UserInformation | undefined
	/** Whether the specified user has consented to receive marketing (required) */
	user_to_receive_marketing?: boolean | undefined
}

/** A booking for an inventory slot */
export interface Booking {
	/** ID of this booking, which must be unique across all bookings. (required) */
	booking_id?: string | undefined
	/**
	 * The appointment slot of this booking
	 * (required for CreateBooking and UpdateBooking:modify,
	 * but not UpdateBooking:cancel)
	 */
	slot?: Slot | undefined
	/**
	 * Personal information of the user making the appointment (required for
	 * CreateBooking)
	 */
	user_information?: UserInformation | undefined
	/**
	 * Status of the booking (required for CreateBooking and UpdateBooking:cancel,
	 * but not UpdateBooking:modify)
	 */
	status?: BookingStatus | undefined
	/**
	 * Information about payment transactions that relate to the booking.
	 * (optional)
	 */
	payment_information?: PaymentInformation | undefined
	/** Information about virtual session related to this booking. (optional) */
	virtual_session_info?: VirtualSessionInfo | undefined
	/**
	 * Information about the Offer applied to this booking.
	 *
	 * Required in CreateBookingResponse if an offer_id was set on the
	 * CreateBookingRequest that created the Booking.
	 */
	offer_info?: OfferInfo | undefined
}

/** Information related to the virtual session which was booked. */
export interface VirtualSessionInfo {
	/** URL which was created for the virtual session. (optional) */
	session_url?: string | undefined
	/** The meeting id which was created for the virtual session. (optional) */
	meeting_id?: string | undefined
	/** Password required to access the session. (optional) */
	password?: string | undefined
}

/**
 * Status data that conveys why (1) creating a lease or (2) creating or updating
 * a booking fails.
 * BookingFailure is intended to primarily capture business logic errors.
 */
export interface BookingFailure {
	/** The reason why the booking failed. (required) */
	cause?: BookingFailure_Cause | undefined
	/** (required only if cause is PAYMENT_ERROR_CARD_TYPE_REJECTED) */
	rejected_card_type?: CreditCardType | undefined
	/**
	 * This optional field is used for the partner to include additional
	 * information for debugging purpose only. (optional)
	 */
	description?: string | undefined
	payment_failure?: BookingFailure_PaymentFailureInformation | undefined
}

export enum BookingFailure_Cause {
	/** CAUSE_UNSPECIFIED - Default value: Don't use; amounts to an "unknown error" */
	CAUSE_UNSPECIFIED = 'CAUSE_UNSPECIFIED',
	/** SLOT_UNAVAILABLE - The referenced availability slot is not available any longer. */
	SLOT_UNAVAILABLE = 'SLOT_UNAVAILABLE',
	/**
	 * SLOT_ALREADY_BOOKED_BY_USER - The user has already booked an appointment for the referenced
	 * availability slot.
	 */
	SLOT_ALREADY_BOOKED_BY_USER = 'SLOT_ALREADY_BOOKED_BY_USER',
	/**
	 * LEASE_EXPIRED - The lease (if provided) has expired and cannot be used any longer to
	 * complete the requested booking.
	 */
	LEASE_EXPIRED = 'LEASE_EXPIRED',
	/**
	 * OUTSIDE_CANCELLATION_WINDOW - The requested cancellation cannot be performed at the current time due
	 * to time restrictions in the merchant's cancellation policy. In other
	 * words, the booking can no longer be canceled by the user.
	 */
	OUTSIDE_CANCELLATION_WINDOW = 'OUTSIDE_CANCELLATION_WINDOW',
	/**
	 * PAYMENT_ERROR_CARD_TYPE_REJECTED - An error was encountered while processing the payment because the
	 * provided credit card type was not accepted by the merchant. The credit
	 * card type must be supplied in rejected_card_type.
	 */
	PAYMENT_ERROR_CARD_TYPE_REJECTED = 'PAYMENT_ERROR_CARD_TYPE_REJECTED',
	/**
	 * PAYMENT_ERROR_CARD_DECLINED - An error was encountered while processing the payment because the
	 * provided credit card was declined.
	 */
	PAYMENT_ERROR_CARD_DECLINED = 'PAYMENT_ERROR_CARD_DECLINED',
	/**
	 * PAYMENT_OPTION_NOT_VALID - An error was encountered with the pack/membership used to pay for the
	 * booking. There could be no valid uses left, it could have expired, etc.
	 */
	PAYMENT_OPTION_NOT_VALID = 'PAYMENT_OPTION_NOT_VALID',
	/**
	 * PAYMENT_ERROR - An error was encountered while processing the payment for this booking.
	 * Use this value to indicate a general payment related error, only if the
	 * error does not match to a specific payment error above.
	 */
	PAYMENT_ERROR = 'PAYMENT_ERROR',
	/**
	 * USER_CANNOT_USE_PAYMENT_OPTION - User cannot use the given payment option (e.g. user trying to use a
	 * first time price for the second time).
	 */
	USER_CANNOT_USE_PAYMENT_OPTION = 'USER_CANNOT_USE_PAYMENT_OPTION',
	/** BOOKING_ALREADY_CANCELLED - A booking that the user tried to cancel has already been cancelled. */
	BOOKING_ALREADY_CANCELLED = 'BOOKING_ALREADY_CANCELLED',
	/** BOOKING_NOT_CANCELLABLE - A booking that the user tried to cancel is not cancellable. */
	BOOKING_NOT_CANCELLABLE = 'BOOKING_NOT_CANCELLABLE',
	/** OVERLAPPING_RESERVATION - User has an existing reservation too close to this time. */
	OVERLAPPING_RESERVATION = 'OVERLAPPING_RESERVATION',
	/**
	 * USER_OVER_BOOKING_LIMIT - Booking failed due to the user being over the aggregator's per-user
	 * bookings limit.
	 */
	USER_OVER_BOOKING_LIMIT = 'USER_OVER_BOOKING_LIMIT',
	/**
	 * OFFER_UNAVAILABLE - Offer (previously "Deal") is unavailable for the provided slot. If the
	 * slot itself is unavailable, use SLOT_UNAVAILABLE instead.
	 */
	OFFER_UNAVAILABLE = 'OFFER_UNAVAILABLE',
	/** @deprecated */
	DEAL_UNAVAILABLE = 'DEAL_UNAVAILABLE',
	/**
	 * PAYMENT_REQUIRES_3DS1 - Set when payment is rejected because you are requesting that the
	 * transaction be tried again, but this time after undergoing 3DS1
	 * challenge/response.  Note that the current transaction's failure state
	 * will stay failed.  The retry will be completely separate.
	 *
	 * When this is the failure reason, payment_failure.3DS1_parameters
	 * MUST be set.  If it is not, then the current cause will be treated as
	 * if it were PAYMENT_ERROR.
	 */
	PAYMENT_REQUIRES_3DS1 = 'PAYMENT_REQUIRES_3DS1',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/** Information about payment failures. */
export interface BookingFailure_PaymentFailureInformation {
	/**
	 * Parameters used by a RwG aggregator to initiate a 3DS1 authentication
	 * protocol with the user.  Will be ignored unless BookingFailure.cause
	 * is set to PAYMENT_REQUIRES_3DS1.
	 */
	threeds1_parameters?: BookingFailure_PaymentFailureInformation_ThreeDS1Parameters | undefined
}

/**
 * Parameters requesting that RwG perform a 3DS1 challenge.
 *
 * The parameters are set by EMVCo's description of the 3DS1 protocol.
 */
export interface BookingFailure_PaymentFailureInformation_ThreeDS1Parameters {
	/**
	 * The URL from which to load a form to present to the User for
	 * authentication.
	 */
	acs_url?: string | undefined
	/**
	 * A PaymentAuthentication Request.  To be posted to the ACSUrl form if
	 * supplied.
	 */
	pa_req?: string | undefined
	/**
	 * An identifier used by the ACS provider.  To be posted to the ACSUrl
	 * form if supplied.
	 */
	transaction_id?: string | undefined
	/** Merchant data.  To be posted to the ACSUrl form if supplied. */
	md_merchant_data?: string | undefined
}

/** Information about payment failures. */
export interface PaymentFailureInformation {
	/**
	 * Parameters used by a RwG aggregator to initiate a 3DS1 authentication
	 * protocol with the user.  Will be ignored unless BookingFailure.cause
	 * is set to PAYMENT_REQUIRES_3DS1.
	 */
	threeds1_parameters?: PaymentFailureInformation_ThreeDS1Parameters | undefined
}

/**
 * Parameters requesting that RwG perform a 3DS1 challenge.
 *
 * The parameters are set by EMVCo's description of the 3DS1 protocol.
 */
export interface PaymentFailureInformation_ThreeDS1Parameters {
	/**
	 * The URL from which to load a form to present to the User for
	 * authentication.
	 */
	acs_url?: string | undefined
	/**
	 * A PaymentAuthentication Request.  To be posted to the ACSUrl form if
	 * supplied.
	 */
	pa_req?: string | undefined
	/**
	 * An identifier used by the ACS provider.  To be posted to the ACSUrl
	 * form if supplied.
	 */
	transaction_id?: string | undefined
	/** Merchant data.  To be posted to the ACSUrl form if supplied. */
	md_merchant_data?: string | undefined
}

/**
 * Parameters requesting that RwG perform a 3DS1 challenge.
 *
 * The parameters are set by EMVCo's description of the 3DS1 protocol.
 */
export interface ThreeDS1Parameters {
	/**
	 * The URL from which to load a form to present to the User for
	 * authentication.
	 */
	acs_url?: string | undefined
	/**
	 * A PaymentAuthentication Request.  To be posted to the ACSUrl form if
	 * supplied.
	 */
	pa_req?: string | undefined
	/**
	 * An identifier used by the ACS provider.  To be posted to the ACSUrl
	 * form if supplied.
	 */
	transaction_id?: string | undefined
	/** Merchant data.  To be posted to the ACSUrl form if supplied. */
	md_merchant_data?: string | undefined
}

/** Resource specification that disambiguates an appointment slot */
export interface ResourceIds {
	/**
	 * The staff ID as provided in the feed or empty if not applicable or no staff
	 * was selected. (optional)
	 */
	staff_id?: string | undefined
	/**
	 * The room ID as provided in the feed or empty if not applicable or no room
	 * was selected. (optional)
	 */
	room_id?: string | undefined
	/**
	 * For Dining Reservations only: the number of seats requested in the booking.
	 * (optional)
	 */
	party_size?: number | undefined
}

/** Information about an Offer applied to a booking. */
export interface OfferInfo {
	/** The ID of the Offer. */
	offer_id?: string | undefined
}

export interface PaymentProcessingParameters {
	/**
	 * The payment processor used to process payment for a given booking.
	 * (required)
	 *
	 * Replaced by the payment_processor field.
	 *
	 * @deprecated
	 */
	processor?: PaymentProcessingParameters_PaymentProcessor | undefined
	/**
	 * The token representing the payment method that will be used to pay
	 * for this booking. This token can be only used once. This token can be
	 * only used for the merchant associated with this booking.
	 *
	 * Each processor may choose its own format for this field.
	 * An example of Stripe's token is "tok_1C3orL2eZvKYlo2CxReMgS4K".
	 *
	 * Replaced by unparsed_payment_method_token, which contains
	 * payment_method_token as one of its fields.
	 * For example, for Stripe, unparsed_payment_method_token is a serialized
	 * JSON object documented at https://stripe.com/docs/api#token_object.
	 * payment_method_token is the 'id' field parsed out of that.
	 *
	 * @deprecated
	 */
	payment_method_token?: string | undefined
	/**
	 * The full token received from Google Payments.  This is typically a
	 * serialized JSON object.  See documentation from Google Payments and your
	 * payment processor for the JSON format of the token for your processor.
	 * https://developers.google.com/pay/api/#participating-google-pay-processors
	 *
	 * This token can only be used once, and only for the merchant associated with
	 * this booking.
	 */
	unparsed_payment_method_token?: string | undefined
	/**
	 * The payment processor API version that the given payment token is valid
	 * for.
	 *
	 * Each processor may choose its own format for this field.
	 * Stripe uses a date (e.g. "2017-06-15"). (required if and only if the
	 * payment processor has a tokenization parameter for version)
	 * This is deprecated in favor of tokenization_parameters.
	 *
	 * @deprecated
	 */
	version?: string | undefined
	/**
	 * The payment processor whose configuration was used to generate this token.
	 * (required)
	 * This is deprecated in favor of tokenization_parameters.
	 *
	 * @deprecated
	 */
	payment_processor?: string | undefined
	/**
	 * The tokenization_config supplied in the Merchant feed that was used
	 * to generate unparsed_payment_method_token.
	 */
	tokenization_config?: TokenizationConfig | undefined
}

export enum PaymentProcessingParameters_PaymentProcessor {
	PAYMENT_PROCESSOR_UNSPECIFIED = 'PAYMENT_PROCESSOR_UNSPECIFIED',
	PROCESSOR_STRIPE = 'PROCESSOR_STRIPE',
	PROCESSOR_BRAINTREE = 'PROCESSOR_BRAINTREE',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/**
 * This describes a payment option, such as a pack, membership, or
 * single-session pass after it has been purchased by a user. It includes an
 * identifier for the user payment option, as well as some information about
 * the payment option with which it is associated.
 */
export interface UserPaymentOption {
	/**
	 * A unique identifier for the user payment option. This Id MUST be unique
	 * for all UserPaymentOptions across all merchants and users. (required)
	 */
	user_payment_option_id?: string | undefined
	/**
	 * The user payment option will be valid (usable) between start_time and
	 * end_time set in UTC. Attempts to use a user payment option to make a
	 * booking outside of this interval will fail. (both optional)
	 */
	valid_start_time_sec?: string | undefined
	valid_end_time_sec?: string | undefined
	/**
	 * The type of the payment option associated with this user payment option.
	 * This can be unlimited for a membership or subscription, multi-use for a
	 * pack, or single-use. (required)
	 */
	type?: PaymentOptionType | undefined
	/**
	 * The original number of uses for this user payment option when it was
	 * purchased. This value is ignored for unlimited payment options. (required)
	 */
	original_count?: number | undefined
	/**
	 * The number of uses remaining for this user payment option. If this number
	 * is 0 for a pack, attempts to use this payment option to make a booking will
	 * fail. (required)
	 */
	current_count?: number | undefined
	/**
	 * The id of the payment option that has been used to purchase this user
	 * payment option. (required)
	 */
	payment_option_id?: string | undefined
}

/**
 * Payment details that are sent when creating a new transaction (which could be
 * a booking, order, or parking session).
 */
export interface PaymentInformation {
	/**
	 * Prepayment status of the transaction.
	 * If the prepayment_status is PREPAYMENT_PROVIDED, then
	 * payment_transaction_id contains the associated unique transaction id for
	 * the purchase.
	 * If the prepayment status is PREPAYMENT_REFUNDED, then
	 * payment_transaction_id contains the associated unique transaction id for
	 * the refund. (required)
	 */
	prepayment_status?: PrepaymentStatus | undefined
	/**
	 * Unique identifier for a payment transaction associated with the booking.
	 * If the payment is PROCESSED_BY_GOOGLE, this field will be set by Google.
	 * If the payment is PROCESSED_BY_PARTNER, this field will be left empty in
	 * Google's CreateBooking or CreateOrder requests to the partner, and it must
	 * be set by the partner in their responses.
	 */
	payment_transaction_id?: string | undefined
	/**
	 * For bookings or orders, these fields must match the service price
	 * (specified in the Services feed) or the PaymentOption corresponding with
	 * this service. They are included in the booking request and response to
	 * verify that the price indicated in the feed has not changed since the last
	 * feed update.
	 *
	 * The price of the transaction, exclusive of any taxes and fees.
	 * Existence of price or taxes does not imply that they have been paid,
	 * prepayment_state should be used for that purpose. (required)
	 */
	price?: Price | undefined
	/**
	 * Taxes that are calculated to be paid for this transaction.
	 * This field can only be absent in one of the following cases:
	 * (1) the price is exempt from or already inclusive of applicable taxes; or
	 * (2) the break down between taxes and fees is not available.
	 * (required when neither of the above holds)
	 */
	tax_amount?: Price | undefined
	/**
	 * Additional fees associated with this transaction, if any.
	 *
	 * The use of this field should be consistent with other pricing related
	 * fields:
	 *  - if a {price, tax, fees} breakdown is provided for service or
	 *  availability, use the same breakdown {price, tax, fees} here.
	 *  - if the price field for service or availability already includes taxes
	 *  or fees, keep using price field to includes taxes or fees and avoid
	 *  setting the tax_amount or fees fields here.
	 * Note: this is only supported for non-order based transactions.
	 */
	fees?: Price | undefined
	/**
	 * Total processing fees & taxes that the user needs to pay for the order;
	 * only applicable to partners that handle order based transactions.
	 * If the order would incur non-zero fees based on
	 * the partner's response during CheckOrderFulfillability, Google would set
	 * this field during CreateOrder; otherwise, the field would be unset.
	 * The partner should verify this value, in addition to the `price` value,
	 * before charging the user.
	 */
	fees_and_taxes?: Price | undefined
	/**
	 * Defines how a deposit may be charged to the user. If there is a deposit,
	 * this field should be set. (optional)
	 */
	deposit?: Deposit | undefined
	/**
	 * Defines a no show fee that may be charged to the user. If the user can be
	 * charged a no show fee, this field should be set. (optional)
	 */
	no_show_fee?: NoShowFee | undefined
	/** Whether the partner or Google processed the payment. (required) */
	payment_processed_by?: PaymentInformation_PaymentProcessedBy | undefined
	/**
	 * The id of the payment option associated with this booking. If this field
	 * is populated, price (and tax_amount, if applicable) must be  populated as
	 * well.
	 */
	payment_option_id?: string | undefined
	/** The id of the user payment option used to pay for this booking. */
	user_payment_option_id?: string | undefined
	/**
	 * Fraud signals that are sent to the partner as per agreement with Google.
	 * The signals are sent via a serialized JSON blob.
	 */
	fraud_signals?: string | undefined
	/**
	 * After we engage in a 3DS1 protocol gaining further authentication
	 * information from a user, this payload is returned to us from an ACS
	 * provider, and it should be used by the partner to complete the 3DS1
	 * protocol.
	 */
	pa_response?: string | undefined
	/**
	 * After we engage in a 3DS1 protocol gaining further authentication
	 * information from a user, this payload may be returned to us from an ACS
	 * provider, and if provided, it should be used by the partner to complete the
	 * 3DS1 protocol.
	 */
	md_merchant_data?: string | undefined
}

/**
 * Who handles payment processing?
 * If payment is processed by the partner, CreateBooking request will
 * include additional parameters (PaymentProcessingParameters) indicating
 * the payment method to be used to process the payment.
 */
export enum PaymentInformation_PaymentProcessedBy {
	PAYMENT_PROCESSED_BY_UNSPECIFIED = 'PAYMENT_PROCESSED_BY_UNSPECIFIED',
	PROCESSED_BY_GOOGLE = 'PROCESSED_BY_GOOGLE',
	PROCESSED_BY_PARTNER = 'PROCESSED_BY_PARTNER',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/** The price of a service or a fee. */
export interface Price {
	/**
	 * The price in micro-units of the currency.
	 * For example: 1.95 USD is 1950000 in micro-units.
	 * If your price contains fractions of the smallest currency unit, then it
	 * will be rounded using nearest even rounding (e.g. 2.5 cents rounded
	 * to 2 cents, 3.5 cents rounded to 4 cents, 0.5 cents rounded to 0 cents,
	 * 2.51 cents rounded to 3 cents). (required)
	 */
	price_micros?: string | undefined
	/** The currency of the price that is defined in ISO 4217. (required) */
	currency_code?: string | undefined
	/**
	 * An optional and opaque string that identifies the pricing option that is
	 * associated with the extended price. (optional)
	 */
	pricing_option_tag?: string | undefined
}

/**
 * A fee that a user may be charged if they have made a booking but do not
 * show up.
 */
export interface NoShowFee {
	/**
	 * The amount the user may be charged if they do not show up for their
	 * reservation.
	 */
	fee?: Price | undefined
	/** Defines how the fee is determined from the availability. */
	fee_type?: PriceType | undefined
}

/**
 * A deposit that the user may be charged or have a hold on their credit card
 * for.
 */
export interface Deposit {
	/** Deposit amount. */
	deposit?: Price | undefined
	/** Minimum advance cancellation for the deposit. */
	min_advance_cancellation_sec?: string | undefined
	/** Defines how the deposit is determined from the availability. */
	deposit_type?: PriceType | undefined
}

/**
 * A configuration for payment-processor tokenization, set up on a per-Merchant
 * basis.
 */
export interface TokenizationConfig {
	/**
	 * A tokenization configuration will typically have one
	 * tokenization_parameter whose key is "gateway" and whose value is the
	 * name of the processor.
	 *
	 * The rest of the parameters are dependent on the processor.  See
	 * documentation from Google Pay and your processor for further information.
	 * https://developers.google.com/pay/api/web/object-reference# \
	 *   PaymentMethodTokenizationSpecification
	 * https://developers.google.com/pay/api/#participating-google-pay-processors
	 *
	 * Braintree example:
	 * tokenization_parameter { key: "gateway" value: "braintree" }
	 * tokenization_parameter { key: "braintree:apiVersion" value: "v1" }
	 * tokenization_parameter { key: "braintree:sdkVersion" value: "2.30.0" }
	 * tokenization_parameter { key: "braintree:merchantId" value: "abcdef" }
	 * tokenization_parameter { key: "braintree:clientKey"
	 *                          value: "production_xxx_yyy" }
	 *
	 * Stripe example:
	 * tokenization_parameter { key: "gateway" value: "stripe" }
	 * tokenization_parameter { key: "stripe:version" value: "2018-02-28" }
	 * tokenization_parameter { key: "stripe:publishableKey" value: "pk_1234" }
	 *
	 * Adyen example:
	 * tokenization_parameter { key: "gateway" value: "adyen" }
	 * tokenization_parameter { key: "gatewayMerchantId" value: "yourId" }
	 */
	tokenization_parameter?: { [key: string]: string } | undefined
	/**
	 * Include in the payment token the user's billing information as entered into
	 * Google Pay with their FOP (see above).
	 */
	billing_information_format?: TokenizationConfig_BillingInformationFormat | undefined
	/**
	 * Name of the Merchant Of Record (MOR).  This user-visible name will be shown
	 * in 3DS2 challenges.  In some cases, this is the Merchant, in others this is
	 * the Aggregator.
	 */
	merchant_of_record_name?: string | undefined
	/** Country where transaction will be processed, in ISO 3166-1 alpha-2 form. */
	payment_country_code?: string | undefined
	/**
	 * Per-Card Network processing parameters.
	 *
	 * These are currently only required for PSD2
	 * (https://en.wikipedia.org/wiki/Payment_Services_Directive)
	 * processing when payment_country_code is a European country where PSD2 is in
	 * effect.  They are also only currently required for VISA and
	 * AMERICAN_EXPRESS.
	 */
	card_network_parameters?: TokenizationConfig_CardNetworkParameters[] | undefined
	/**
	 * Defines types of cardholder data that are accepted by the gateway.
	 * If not specified, no restrictions are applied.
	 *
	 * Note that partners who use commercial gateways should leave this
	 * empty unless their gateway provider has specified otherwise.  Restricting
	 * allowed_auth_methods is most useful in the scenario that a partner
	 * integrates with GPay as a gateway themselves.
	 */
	allowed_auth_methods?: TokenizationConfig_AuthMethod[] | undefined
}

/**
 * How much of the Billing Address to require of the user and include in the
 * token. The enum values correspond to parameters in the Google Pay API (see
 * https://developers.google.com/pay/api/web/reference/object\
 *  #BillingAddressParameters).
 */
export enum TokenizationConfig_BillingInformationFormat {
	BILLING_INFORMATION_FORMAT_UNSPECIFIED = 'BILLING_INFORMATION_FORMAT_UNSPECIFIED',
	/** MIN - name, country code, and postal code (GPay default setting). */
	MIN = 'MIN',
	/** FULL - name, street address, locality, region, country code, and postal code */
	FULL = 'FULL',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/**
 * Fields supported to authorize a card transaction.
 *
 * See the GPay documentation at
 * https://developers.google.com/pay/api/web/reference/object#CardParameters
 */
export enum TokenizationConfig_AuthMethod {
	AUTH_METHOD_UNSPECIFIED = 'AUTH_METHOD_UNSPECIFIED',
	/**
	 * PAN_ONLY - This authentication method is associated with payment cards stored on
	 * file with the user's Google Account. Returned payment data includes
	 * personal account number (PAN) with the expiration month and the
	 * expiration year.
	 */
	PAN_ONLY = 'PAN_ONLY',
	/**
	 * CRYPTOGRAM_3DS - This authentication method is associated with cards stored as Android
	 * device tokens. Returned payment data includes a 3-D Secure (3DS)
	 * cryptogram generated on the device.
	 */
	CRYPTOGRAM_3DS = 'CRYPTOGRAM_3DS',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

export interface TokenizationConfig_TokenizationParameterEntry {
	key: string
	value: string
}

/** Per CardNetwork Processing information. */
export interface TokenizationConfig_CardNetworkParameters {
	/** The Card Network that these parameters are about */
	card_network?: CreditCardRestrictions_CreditCardType | undefined
	/**
	 * The Bank Identification Number of the acquiring bank used for processing
	 * of the card.
	 *
	 * If this value is not known to you, you should ask your acquirer or
	 * merchant processor representative.
	 */
	acquirer_bin?: string | undefined
	/**
	 * The merchant identifier assigned by the acquirer to the merchant for use
	 * in transaction authorization (for Visa and American Express
	 * transactions).
	 *
	 * If this value is not known to you, you should ask the acquirer or
	 * merchant processor representative.
	 */
	acquirer_merchant_id?: string | undefined
}

/** Restrictions to the credit card types this merchant accepts. */
export interface CreditCardRestrictions {
	/**
	 * A list of supported credit cards. No credit cards are supported if empty.
	 * (optional)
	 */
	credit_card_type?: CreditCardRestrictions_CreditCardType[] | undefined
}

/** A credit card type. */
export enum CreditCardRestrictions_CreditCardType {
	/** CREDIT_CARD_TYPE_UNSPECIFIED - Unused. */
	CREDIT_CARD_TYPE_UNSPECIFIED = 'CREDIT_CARD_TYPE_UNSPECIFIED',
	/** VISA - A Visa credit card. */
	VISA = 'VISA',
	/** MASTERCARD - A Mastercard credit card. */
	MASTERCARD = 'MASTERCARD',
	/** AMERICAN_EXPRESS - An American Express credit card. */
	AMERICAN_EXPRESS = 'AMERICAN_EXPRESS',
	/** DISCOVER - A Discover credit card. */
	DISCOVER = 'DISCOVER',
	/** JCB - A JCB credit card. */
	JCB = 'JCB',
	UNRECOGNIZED = 'UNRECOGNIZED',
}

/** An inventory slot */
export interface Slot {
	/** ID of the merchant for the slot (required for CreateBooking) */
	merchant_id?: string | undefined
	/** ID of the merchant Service (required for CreateBooking) */
	service_id?: string | undefined
	/**
	 * Start time of the appointment slot in seconds of UTC time since Unix epoch.
	 * (required for CreateBooking)
	 */
	start_sec?: string | undefined
	/** Duration of the appointment slot (required for CreateBooking) */
	duration_sec?: string | undefined
	/**
	 * Opaque tag that identifies the availability slot and matches the value
	 * provided in the availability feed (optional)
	 */
	availability_tag?: string | undefined
	/**
	 * The set of resources that disambiguates the appointment slot, e.g. by
	 * indicating the staff member and room selected by the user (optional)
	 */
	resources?: ResourceIds | undefined
	/**
	 * Indicates whether bookings of this slot will be confirmed
	 * synchronously or asynchronously. (optional)
	 * An UNSPECIFIED value will be interpreted as synchronous.
	 */
	confirmation_mode?: ConfirmationMode | undefined
}

/**
 * Personal information about the person taking action (e.g. making a
 * booking, an order, or creates a parking session).
 */
export interface UserInformation {
	/**
	 * Unique ID of the user to the partner, chosen by Reserve with Google.
	 * (required)
	 */
	user_id?: string | undefined
	/** Given name of the user (maximum 40 characters) (required) */
	given_name?: string | undefined
	/** Family name of the user (maximum 40 characters) (required) */
	family_name?: string | undefined
	/** Address of the user (optional) */
	address?: PostalAddress | undefined
	/**
	 * Phone number of the user (required)
	 * Consistent with the international definition in ITU-T E.123 recommendation.
	 * However, local conventions are also followed, such as using '-' instead of
	 * a space as separator. For example, a phone number in the US can be
	 * written as '+1 415-736-0000'
	 */
	telephone?: string | undefined
	/** Email address of the user (required except for waitlists) */
	email?: string | undefined
	/**
	 * User's language code, in IETF BCP 47 format. It is sent only if a partner
	 * is allowed to use this feature. Please contact Reserve with Google team
	 * to be added to the allowlist and receive this code. (optional)
	 */
	language_code?: string | undefined
}

/** The postal address for a merchant. */
export interface PostalAddress {
	/** The country, using ISO 3166-1 alpha-2 country code, e.g. "US" (required) */
	country?: string | undefined
	/** The locality/city, e.g. "Mountain View". (required) */
	locality?: string | undefined
	/**
	 * The region/state/province, e.g. "CA". This field is only required in
	 * countries where region is commonly a part of the address. (optional)
	 */
	region?: string | undefined
	/** The postal code, e.g. "94043". (required) */
	postal_code?: string | undefined
	/** The street address, e.g. "1600 Amphitheatre Pkwy". (required) */
	street_address?: string | undefined
}
